<script setup lang="ts">
import { useDropZone, useFileDialog } from '@vueuse/core'

const props = defineProps<{
  accept: string
  disabled?: boolean
  /** Процент загрузки файла. Пока идёт upload, вместо выбора файла показывается прогресс. */
  progress?: number | null
  /** Имя файла, который сейчас загружается. */
  fileName?: string | null
}>()

const emit = defineEmits<{
  file: [file: File]
}>()

const dropZoneRef = ref<HTMLDivElement>()
const { open, onChange } = useFileDialog({
  multiple: false,
})

function takeFirst(files: File[] | FileList | null) {
  if (props.disabled || props.progress != null || !files?.length)
    return
  const file = files[0]
  if (file)
    emit('file', file)
}

function openDialog() {
  if (props.disabled || props.progress != null)
    return
  open({ accept: props.accept || undefined, multiple: false })
}

onChange(files => {
  takeFirst(files)
})

function onDrop(files: File[] | null) {
  takeFirst(files)
}

useDropZone(dropZoneRef, onDrop)
</script>

<template>
  <div
    ref="dropZoneRef"
    class="d-flex flex-column align-center justify-center ga-4 pa-12 import-drop"
    :class="{ 'import-drop--disabled': disabled && progress == null }"
    @click="openDialog"
  >
    <div v-if="progress != null" class="d-flex flex-column align-center ga-3 w-100">
      <AnimatedLogoLoader />
      <span v-if="fileName" class="text-body-1 text-medium-emphasis text-center text-break w-100">
        {{ fileName }}
      </span>
      <span class="text-h5 text-center">{{ Math.ceil(progress) }}%</span>
      <VProgressLinear
        class="w-100"
        :model-value="progress"
        color="primary"
        height="8"
        rounded
      />
    </div>
    <template v-else>
      <VIcon
        class="import-drop-icon"
        icon="ri-download-line"
        size="40"
      />
      <h4 class="text-h4 mb-0">
        Загрузка файла
      </h4>
      <span class="text-body-1 text-medium-emphasis text-center">
        Перетащите файл сюда или выберите его для загрузки
      </span>
      <VBtn
        variant="outlined"
        color="secondary"
        :disabled="disabled"
        @click.stop="openDialog"
      >
        Выбрать файл
      </VBtn>
    </template>
  </div>
</template>

<style scoped lang="scss">
.import-drop {
  border: 2px dashed rgba(var(--v-theme-on-surface), var(--v-border-opacity));
  border-radius: 8px;
  background-color: rgb(var(--v-theme-surface));
  cursor: pointer;
}

.import-drop-icon {
  color: rgba(var(--v-theme-text-primary), var(--v-activated-opacity));
}

.import-drop--disabled {
  cursor: default;
  opacity: 0.6;
}
</style>
